import { CommonModule } from '@angular/common';
import { ErrorHandler, ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { RouterStateSerializer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { FormsModule } from '@angular/forms';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { OAuthModule } from 'angular-oauth2-oidc';

import { environment } from '@env/environment';

import { AppState, metaReducers, reducers, selectRouterState } from './core.state';
import { AuthEffects } from './auth/auth.effects';
import { selectAuth, selectIsAuthenticated, selectUserProfile } from './auth/auth.selectors';
import { authLogin, authLogout } from './auth/auth.actions';
import { AuthGuardService } from './auth/auth-guard.service';
import { AppErrorHandler } from './error-handler';
import { CustomSerializer } from './router/custom-serializer';
import { LocalStorageService } from './local-storage/local-storage.service';
import { HttpErrorInterceptor } from './http-interceptors/http-error.interceptor';
import { NotificationService } from './notifications/notification.service';
import { AuthInterceptor } from './auth/auth.interceptor';
import {EmployeeEffects} from "./employee/employee.effect";

export {
  selectAuth,
  authLogin,
  authLogout,
  AppState,
  LocalStorageService,
  selectIsAuthenticated,
  selectUserProfile,
  AuthGuardService,
  selectRouterState,
  NotificationService,
};

const INTERCEPTOR_PROVIDES = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpErrorInterceptor,
    multi: true,
  },
  {
    provide: ErrorHandler,
    useClass: AppErrorHandler,
  },
  {
    provide: RouterStateSerializer,
    useClass: CustomSerializer,
  },
];

@NgModule({
  imports: [
    MatSnackBarModule,
    CommonModule,
    HttpClientModule,
    FormsModule,
    OAuthModule.forRoot(),
    StoreModule.forRoot(reducers, { metaReducers }),
    StoreRouterConnectingModule.forRoot(),
    EffectsModule.forRoot([AuthEffects, EmployeeEffects]),
    environment.production
      ? []
      : StoreDevtoolsModule.instrument({
          name: 'Artstuff Portal',
        }),
  ],
  declarations: [],
  providers: [],
  exports: [],
})
export class AesCoreModule {
  static forRoot(): ModuleWithProviders<AesCoreModule> {
    return {
      ngModule: AesCoreModule,
      providers: INTERCEPTOR_PROVIDES,
    };
  }

  constructor(@Optional() @SkipSelf() parentModule: AesCoreModule) {
    if (parentModule) {
      throw new Error('CoreModule is already loaded. Import only in AppModule');
    }
  }
}
